import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Download, Plus, Search, Ban, ArrowUpRight, ArrowDownRight, Wallet, Receipt, Loader2, X, LogIn, LogOut } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";
import { Toaster, toast } from "sonner";
import { AdminShell } from "@/components/AdminShell";
import { Panel, Eyebrow, MonoLabel } from "@/components/Panel";
import { ReceiptThumb } from "@/components/ReceiptThumb";
import { statisticsApi, rupees } from "@/lib/statisticsApi";
import { staffLogin, ADMIN_TOKEN_KEY, apiErr } from "@/lib/authApi";

export default function Statistics() {
  const [authed, setAuthed] = useState(!!localStorage.getItem(ADMIN_TOKEN_KEY));
  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;
  return <Dashboard onLogout={() => { localStorage.removeItem(ADMIN_TOKEN_KEY); setAuthed(false); }} />;
}

function Dashboard({ onLogout }) {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ type: "", category: "", from: "", to: "", q: "", include_voided: false });
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cats, setCats] = useState([]);

  const load = async () => {
    setBusy(true);
    try {
      const [s, l, c] = await Promise.all([
        statisticsApi.summary({ include_voided: filters.include_voided }),
        statisticsApi.ledger({
          ...(filters.type ? { type: filters.type } : {}),
          ...(filters.category ? { category: filters.category } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          include_voided: filters.include_voided,
          page_size: 200,
        }),
        statisticsApi.categories(),
      ]);
      setSummary(s); setRows(l.items || []); setTotal(l.total || 0); setCats(c.items || []);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); }, [filters.type, filters.category, filters.from, filters.to, filters.include_voided]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 350); return () => clearTimeout(t); }, [filters.q]); // eslint-disable-line

  return (
    <AdminShell>
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "var(--panel)", border: "1px solid var(--hairline)", color: "var(--text)", borderRadius: 0 } }} />
      <div className="max-w-[1280px] mx-auto px-4 sm:px-8 py-10">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Eyebrow>Admin · Financial Ledger</Eyebrow>
            <h1 className="h-hero mt-3 text-4xl sm:text-5xl">The books, in real time.</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="stats-export-btn" className="btn-ghost" onClick={() => statisticsApi.downloadExport({ format: "xlsx" }).catch((e) => toast.error(apiErr(e)))}>
              <Download size={14} /> Export XLSX
            </button>
            <button data-testid="stats-export-csv-btn" className="btn-ghost" onClick={() => statisticsApi.downloadExport({ format: "csv" }).catch((e) => toast.error(apiErr(e)))}>
              <Download size={14} /> Export CSV
            </button>
            <button data-testid="stats-add-expense-btn" className="btn-red" onClick={() => setAddOpen(true)}>
              <Plus size={14} /> Add expense
            </button>
            <button data-testid="stats-logout-btn" className="btn-ghost" onClick={onLogout}><LogOut size={14} /> Sign out</button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI icon={<ArrowUpRight size={16} />} k="Total Income" v={rupees(summary?.total_income_paise)} color="green" testid="kpi-income" />
          <KPI icon={<ArrowDownRight size={16} />} k="Total Expense" v={rupees(summary?.total_expense_paise)} color="red" testid="kpi-expense" />
          <KPI icon={<Wallet size={16} />} k="Balance" v={rupees(summary?.balance_paise)} color={(summary?.balance_paise || 0) >= 0 ? "green" : "red"} testid="kpi-balance" />
          <KPI icon={<Receipt size={16} />} k={`Razorpay Net · after ~${summary?.razorpay_fees_pct ?? 2}% fees`} v={rupees(summary?.razorpay_net_paise)} sub={`Gross ${rupees(summary?.razorpay_gross_paise)} · Fees ${rupees(summary?.razorpay_fees_estimated_paise)}`} testid="kpi-rzp-net" />
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6">
          <Panel className="p-6 lg:col-span-3">
            <MonoLabel>Expenses by category · budget vs actual</MonoLabel>
            <ExpenseChart data={summary?.expense_by_category || []} />
          </Panel>
          <Panel className="p-6 lg:col-span-2">
            <MonoLabel>Income by category</MonoLabel>
            <div className="mt-4 space-y-3">
              {(summary?.income_by_category || []).length === 0 && <div className="mono-label" style={{ color: "var(--taupe)" }}>No income yet</div>}
              {(summary?.income_by_category || []).map((c) => (
                <div key={c.category} className="flex items-center justify-between hairline p-3">
                  <div className="text-sm">{c.category}</div>
                  <div className="font-serif" style={{ color: "var(--green)" }}>{rupees(c.amount_paise)}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>

        <Panel className="mt-8 p-0 overflow-hidden">
          <div className="p-5 hairline-b flex flex-wrap items-center gap-3">
            <MonoLabel>Ledger · {total} {total === 1 ? "entry" : "entries"}</MonoLabel>
            <div className="ml-auto flex flex-wrap gap-2">
              <div className="flex items-center gap-2 hairline px-3 py-1.5">
                <Search size={12} style={{ color: "var(--taupe)" }} />
                <input data-testid="ledger-search" placeholder="Search description / ref" className="bg-transparent text-sm outline-none w-52" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
              </div>
              <select data-testid="ledger-type-filter" className="input-x !py-1.5 !w-auto text-sm" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
                <option value="">All types</option>
                <option value="income">Income</option>
                <option value="expense">Expense</option>
              </select>
              <select data-testid="ledger-cat-filter" className="input-x !py-1.5 !w-auto text-sm" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
                <option value="">All categories</option>
                <option value="registration_fee">registration_fee</option>
                {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
              <input data-testid="ledger-from" type="date" className="input-x !py-1.5 !w-auto text-sm" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
              <input data-testid="ledger-to" type="date" className="input-x !py-1.5 !w-auto text-sm" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
              <label className="flex items-center gap-2 mono-label" style={{ color: "var(--taupe)" }}>
                <input data-testid="ledger-voided-toggle" type="checkbox" checked={filters.include_voided} onChange={(e) => setFilters({ ...filters, include_voided: e.target.checked })} />
                Voided
              </label>
            </div>
          </div>

          <FilterSubtotals rows={rows} />

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left mono-label border-b border-[var(--hairline)]">
                  <th className="py-3 px-4">Date</th>
                  <th className="py-3 px-4">Type</th>
                  <th className="py-3 px-4">Category</th>
                  <th className="py-3 px-4">Description</th>
                  <th className="py-3 px-4 text-right">Amount</th>
                  <th className="py-3 px-4">Receipt</th>
                  <th className="py-3 px-4">Ref</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {busy && rows.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center mono-label" style={{ color: "var(--taupe)" }}><Loader2 size={14} className="animate-spin inline mr-2" />Loading…</td></tr>
                )}
                {!busy && rows.length === 0 && (
                  <tr><td colSpan={8} className="py-8 text-center mono-label" style={{ color: "var(--taupe)" }}>No entries match these filters</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="hairline-t" data-testid={`ledger-row-${r.id}`} style={{ opacity: r.void ? 0.55 : 1 }}>
                    <td className="py-3 px-4 mono-label">{r.entry_date}</td>
                    <td className="py-3 px-4">
                      <span className="chip" style={{ color: r.type === "income" ? "var(--green)" : "var(--red)", borderColor: r.type === "income" ? "var(--green)" : "var(--red)" }}>
                        {r.type.toUpperCase()}
                      </span>
                      {r.void && <span className="ml-2 chip" style={{ color: "var(--taupe)", borderColor: "var(--taupe)" }}>VOID</span>}
                    </td>
                    <td className="py-3 px-4">{r.category}</td>
                    <td className="py-3 px-4 max-w-[380px] truncate" title={r.description}>
                      {r.description}
                      {r.void && r.void_reason && (<div className="mono-label" style={{ color: "var(--taupe)" }}>Voided · {r.void_reason}</div>)}
                    </td>
                    <td className="py-3 px-4 text-right font-serif" style={{ color: r.type === "income" ? "var(--green)" : "var(--red-2)" }}>
                      {r.type === "income" ? "+" : "−"} {rupees(r.amount_paise)}
                    </td>
                    <td className="py-3 px-4">
                      <ReceiptThumb receiptUrl={r.receipt_url} contentType={r.receipt_content_type} originalName={r.receipt_original_name} />
                    </td>
                    <td className="py-3 px-4 mono-label truncate max-w-[160px]" style={{ color: "var(--taupe)" }}>{r.payment_ref || "—"}</td>
                    <td className="py-3 px-4 text-right">
                      {r.type === "expense" && !r.void && (
                        <button data-testid={`void-btn-${r.id}`} onClick={() => voidRow(r, load)} className="p-2 hover:text-[var(--red)]"><Ban size={14} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {addOpen && <AddExpenseModal cats={cats} onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
    </AdminShell>
  );
}

function voidRow(r, reload) {
  const reason = window.prompt(`Void this expense of ${rupees(r.amount_paise)}?\n\nEnter a reason (min 3 chars):`);
  if (!reason || reason.trim().length < 3) return;
  statisticsApi.voidExpense(r.id, reason.trim())
    .then(() => { toast.success("Expense voided · history preserved"); reload(); })
    .catch((e) => toast.error(apiErr(e)));
}

function FilterSubtotals({ rows }) {
  const totals = useMemo(() => {
    let income = 0, expense = 0, voided = 0, voidedPaise = 0;
    for (const r of rows) {
      if (r.void) { voided += 1; voidedPaise += r.amount_paise; continue; }
      if (r.type === "income") income += r.amount_paise;
      else if (r.type === "expense") expense += r.amount_paise;
    }
    return { income, expense, net: income - expense, voided, voidedPaise, count: rows.length };
  }, [rows]);

  if (!rows.length) return null;

  return (
    <div className="px-5 py-3 hairline-b flex flex-wrap items-center gap-x-6 gap-y-2 text-xs" data-testid="ledger-subtotals" style={{ background: "var(--panel-2)" }}>
      <span className="mono-label" style={{ color: "var(--taupe)" }}>Filtered subtotals ·</span>
      <Sub k="Rows" v={totals.count} testid="sub-count" />
      <Sub k="Income" v={rupees(totals.income)} color="var(--green)" testid="sub-income" />
      <Sub k="Expense" v={rupees(totals.expense)} color="var(--red-2)" testid="sub-expense" />
      <Sub k="Net" v={rupees(totals.net)} color={totals.net >= 0 ? "var(--green)" : "var(--red-2)"} testid="sub-net" />
      {totals.voided > 0 && (
        <Sub k={`Voided (${totals.voided})`} v={rupees(totals.voidedPaise)} color="var(--taupe)" testid="sub-voided" />
      )}
    </div>
  );
}

function Sub({ k, v, color, testid }) {
  return (
    <span className="inline-flex items-baseline gap-1.5" data-testid={testid}>
      <span className="mono-label" style={{ color: "var(--taupe)" }}>{k}</span>
      <span className="font-serif" style={{ color: color || "var(--text)" }}>{v}</span>
    </span>
  );
}

function KPI({ icon, k, v, sub, color = "text", testid }) {
  const col = color === "green" ? "var(--green)" : color === "red" ? "var(--red-2)" : "var(--text)";
  return (
    <Panel className="p-5" data-testid={testid}>
      <div className="flex items-center gap-2" style={{ color: "var(--taupe)" }}>
        {icon}<span className="mono-label">{k}</span>
      </div>
      <div className="font-serif text-3xl mt-2" style={{ color: col }}>{v || "—"}</div>
      {sub && <div className="mono-label mt-1" style={{ color: "var(--muted)" }}>{sub}</div>}
    </Panel>
  );
}

function ExpenseChart({ data }) {
  const chartData = useMemo(
    () => data.map((d) => ({
      name: d.category, actual: (d.amount_paise || 0) / 100, budget: (d.budget_paise || 0) / 100,
      pct: d.budget_used_pct,
    })),
    [data]
  );
  if (!chartData.length) return <div className="mono-label mt-6" style={{ color: "var(--taupe)" }}>No expenses recorded yet</div>;
  return (
    <div className="mt-4" style={{ width: "100%", height: 280 }}>
      <ResponsiveContainer>
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
          <XAxis dataKey="name" tick={{ fill: "var(--taupe)", fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
          <YAxis tick={{ fill: "var(--taupe)", fontSize: 11 }} tickFormatter={(v) => `₹${v.toLocaleString("en-IN")}`} />
          <Tooltip
            contentStyle={{ background: "var(--panel)", border: "1px solid var(--hairline)", borderRadius: 0 }}
            formatter={(v, name) => [`₹${Number(v).toLocaleString("en-IN")}`, name === "actual" ? "Spent" : "Budget"]}
          />
          <Bar dataKey="budget" fill="var(--hairline)" name="budget" />
          <Bar dataKey="actual" name="actual">
            {chartData.map((d, i) => (
              <Cell key={i} fill={d.pct !== null && d.pct > 100 ? "var(--red)" : "var(--red-2)"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function AddExpenseModal({ cats, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [category, setCategory] = useState(cats[0]?.name || "");
  const [rupeesAmt, setRupees] = useState("");
  const [description, setDescription] = useState("");
  const [entryDate, setEntryDate] = useState(today);
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!category.trim()) return toast.error("Category is required");
    const amtPaise = Math.round(Number(rupeesAmt) * 100);
    if (!amtPaise || amtPaise <= 0) return toast.error("Enter a valid amount in ₹");
    if (!description.trim()) return toast.error("Description is required");
    setBusy(true);
    const fd = new FormData();
    fd.append("category", category.trim());
    fd.append("amount_paise", String(amtPaise));
    fd.append("description", description.trim());
    fd.append("entry_date", entryDate);
    if (receipt) fd.append("receipt", receipt);
    try {
      await statisticsApi.createExpense(fd);
      toast.success(`Expense recorded · ${rupees(amtPaise)}`);
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4">
      <Panel className="w-full max-w-lg p-6 relative" data-testid="add-expense-modal">
        <button data-testid="add-expense-close" onClick={onClose} className="absolute right-4 top-4 p-1 hover:text-[var(--red)]"><X size={16} /></button>
        <MonoLabel>Add Expense</MonoLabel>
        <h2 className="font-serif text-2xl mt-2">Log a new expense</h2>

        <div className="mt-5 space-y-4">
          <Field label="Category">
            <input data-testid="exp-category" list="cat-options" className="input-x" placeholder="e.g. venue, catering, printing"
                   value={category} onChange={(e) => setCategory(e.target.value)} />
            <datalist id="cat-options">
              {cats.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Amount (₹)">
              <input data-testid="exp-amount" type="number" step="0.01" min="0" className="input-x" placeholder="0.00" value={rupeesAmt} onChange={(e) => setRupees(e.target.value)} />
            </Field>
            <Field label="Date">
              <input data-testid="exp-date" type="date" className="input-x" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} />
            </Field>
          </div>
          <Field label="Description">
            <textarea data-testid="exp-description" rows={2} className="input-x" placeholder="Vendor / purpose" value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label="Receipt (optional · jpg/png/webp/pdf · max 5 MB)">
            <input data-testid="exp-receipt" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={(e) => setReceipt(e.target.files?.[0] || null)} className="text-sm" />
          </Field>
        </div>

        <div className="mt-6 flex gap-3">
          <button data-testid="exp-submit" onClick={submit} disabled={busy} className="btn-red">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {busy ? "Saving…" : "Save expense"}
          </button>
          <button onClick={onClose} className="btn-ghost">Cancel</button>
        </div>
      </Panel>
    </motion.div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="mono-label mb-2">{label}</div>
      {children}
    </label>
  );
}

function LoginGate({ onSuccess }) {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr(""); setBusy(true);
    try {
      const r = await staffLogin(phone.replace(/\D/g, "").slice(-10), password);
      localStorage.setItem(ADMIN_TOKEN_KEY, r.token);
      onSuccess();
    } catch (e) { setErr(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" />
      <div className="max-w-[440px] mx-auto px-4 sm:px-8 py-16">
        <div className="flex justify-center mb-6" data-testid="stats-login-logo-wrap">
          <img src="/risse-bni-dark.webp" alt="RISSE BNI 26 · Davanagere Members Day" className="h-28 w-auto" data-testid="stats-login-logo" />
        </div>
        <Eyebrow>Statistics · Admin</Eyebrow>
        <h1 className="h-hero mt-3 text-3xl">Staff sign-in</h1>
        <Panel className="p-6 mt-6">
          <MonoLabel>Admin / DevAdmin phone</MonoLabel>
          <input data-testid="stats-login-phone" className="input-x mt-3" placeholder="10-digit phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          <MonoLabel className="mt-4 block">Password</MonoLabel>
          <input data-testid="stats-login-password" type="password" className="input-x mt-3" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
          {err && <div data-testid="stats-login-err" className="text-sm mt-3" style={{ color: "var(--red-2)" }}>{err}</div>}
          <button data-testid="stats-login-btn" onClick={submit} disabled={busy} className="btn-red mt-5 w-full">
            <LogIn size={14} /> {busy ? "Signing in…" : "Sign in"}
          </button>
        </Panel>
      </div>
    </div>
  );
}
